import { FC, useMemo } from "react";
import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import { InternalBillingExplorerChart } from "./InternalBillingExplorerChart";
import { BillingExplorerChartBaseSelect } from "../../../../pages/resource-explorer/components/settings-panel/components/chart-settings-section/components/BillingExplorerChartBaseSelect";
import {
  DateDataPoint,
  ResourceExplorer,
  ResourceExplorerWidget,
} from "@/services/cloudchipr.api";
import { HighchartsChartTypeSelect } from "@/storybook/c8r-highcharts/common/components/HighchartsChartTypeSelect";
import { billingExplorerXAxisOptions } from "@/components/pages/resource-explorer/components/app-bar/components/resource-explorer-toolbar/components/x-axis-selector/utils/constants";
import { GroupingOptionIcon } from "@/components/pages/common/resource-explorer-grouping-selector-v2/components/GroupingOptionIcon";

interface InternalResourceExplorerChartProps {
  dataPoint: DateDataPoint;
  showBudgetLine: boolean;
  view: ResourceExplorer;
  viewData: ResourceExplorerWidget;
}

export const InternalBillingExplorerChartSection: FC<
  InternalResourceExplorerChartProps
> = ({ dataPoint, showBudgetLine, view, viewData }) => {
  const selectedGranularity = useMemo(() => {
    return billingExplorerXAxisOptions.find(
      ({ value }) => value === view.data.axis_granularity,
    );
  }, [view.data.axis_granularity]);

  return (
    <Stack>
      <Stack
        px={1}
        spacing={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <BillingExplorerChartBaseSelect
            base={"cost"}
            onChartBaseChange={placeholder}
          />

          {selectedGranularity && (
            <TextField
              size="xsmall"
              label="Granularity(X-Axis)"
              contentEditable={false}
              value={selectedGranularity.label}
              InputProps={{
                readOnly: true,
                inputProps: {
                  style: {
                    paddingLeft: 8,
                    cursor: "pointer",
                    caretColor: "transparent",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  },
                },
                sx: { pr: 1, cursor: "pointer", minWidth: 180 },
                startAdornment: (
                  <GroupingOptionIcon grouping={selectedGranularity.value} />
                ),
                endAdornment: <ArrowDropDownOutlinedIcon color="action" />,
              }}
            />
          )}

          {dataPoint && (
            <FormControl size="xsmall" fullWidth sx={{ width: 110 }}>
              <Select
                value={dataPoint}
                name="data_point"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "grey.300",
                  },
                }}
              >
                <MenuItem value="daily">
                  <Typography variant="caption">Daily</Typography>
                </MenuItem>
                <MenuItem value="weekly">
                  <Typography variant="caption">Weekly</Typography>
                </MenuItem>
                <MenuItem value="monthly">
                  <Typography variant="caption">Monthly</Typography>
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </Stack>

        <Stack spacing={1} direction="row" alignItems="center">
          <HighchartsChartTypeSelect
            size="xsmall"
            selectionType="select"
            chartType="stacked_column"
            onChartTypeChange={placeholder}
            availableTypes={["line", "column", "stacked_column", "pie"]}
          />
        </Stack>
      </Stack>

      <InternalBillingExplorerChart
        view={view}
        viewData={viewData}
        showBudgetLine={showBudgetLine}
      />
    </Stack>
  );
};

const placeholder = () => {};
